// Display formatting for a date scalar cell.
//
// The payload states its own granularity (#11055), so this reads what the value
// says it is instead of inferring a kind from the shape of the string. A
// calendar date renders verbatim for every viewer; an instant renders as the
// day it falls on in the zone the value names -- the viewer's for a grid
// column, the interaction's own for an occurrence.
//
// The old inference read every timestamp in UTC. That is the reported bug: an
// upload stamped 2026-07-30T00:00:07Z showed as July 30 to a client in Hawaii,
// where it was still July 29, and the two uploads seventeen seconds either side
// of UTC midnight landed on different days.

import {
  calendarDaysBetween,
  formatDateCellScalar,
  isTemporal,
  temporalDayIn,
  temporalFromLegacyString,
  todayInZone,
  type Temporal,
} from "@/utils/temporal";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";

const pad = (n: number): string => String(n).padStart(2, "0");

const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// The value an elapsed measurement counts from, for the shapes a cell can
// hold, stated as a temporal so the count below never has to ask what kind of
// time it is looking at.
//
// A `Date` is an instant -- an epoch value is the one thing `Date` models
// honestly -- so it says so, and carries the viewer's zone because a machine
// timestamp has no zone of its own to declare. Anything else is a string from
// a column that has not adopted the wire contract; it goes through the one
// sanctioned shape-inference rather than a second private copy of it, so this
// cell can never disagree with the file tree about what a given string is.
const elapsedFrom = (value: unknown, viewerZone: string): Temporal | null => {
  if (value instanceof Date)
    return Number.isNaN(value.getTime())
      ? null
      : { granularity: "instant", at: value.toISOString(), zone: viewerZone };
  if (isTemporal(value)) return value;
  return temporalFromLegacyString(String(value), viewerZone);
};

// Display formatting for a date scalar carrying the `elapsed` unit
// (`scalar_unit === "elapsed"`, #10812) -- how long ago the instant was,
// rather than the instant itself.
//
// The stored value stays an absolute date; only this rendering is relative,
// which is what keeps a cached row or a CSV export from silently ageing.
// Granularity is deliberately coarse: relationship freshness is read at a
// glance ("is this manager going stale?"), so days/months/years carry the
// signal and hours would be noise.
//
// The count is a difference between two *calendar days*, never between two
// moments (#11055). Two things follow, and both were wrong before:
//
//   - A calendar date has no moment to subtract. Reading `2026-07-30` through
//     `new Date` made it midnight UTC, so a viewer in Hawaii counted an extra
//     day and one in Tokyo counted one too few -- visible precisely at the
//     bottom of the ladder, where an interaction logged today read "Yesterday".
//   - An instant has a moment but no day until a zone is named, and the zone
//     that matters here is the *reader's*: "how long ago" is asked from where
//     they are sitting, so their today is the other end of the subtraction.
//
// `viewerZone` is injected alongside `now` so a test can sit a reader in a
// given zone; both default to this browser's answer.
export const formatElapsedScalar = (
  value: unknown,
  now: Date = new Date(),
  viewerZone: string = currentViewerTimeZone()
): string => {
  if (value == null) return "";
  const from = elapsedFrom(value, viewerZone);
  if (!from) return formatDateScalar(value);

  const days = calendarDaysBetween(
    temporalDayIn(from, viewerZone),
    todayInZone(viewerZone, now)
  );
  // Only reachable if a day that parsed on the way in stops parsing here.
  if (days === null) return formatDateScalar(value);
  // A future date means the interaction is scheduled ahead of now. Rather
  // than render a negative age, fall back to the absolute date.
  if (days < 0) return formatDateScalar(value);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;

  // One ladder, thresholded on `days` throughout. Deriving the months rung
  // from days/30 but the years rung from days/365 leaves a gap: at 360 days
  // the month count reaches 12 while the year count is still 0, which
  // rendered "0 years ago" for days 360-364.
  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

// `unit` is the column's `scalar_unit`: `datetime` (Uploaded At, #14483) adds
// an instant's clock and zone to its day.
export const formatDateScalar = (
  value: unknown,
  unit?: string | null
): string => {
  // A Date object carries no source-zone intent; keep its local calendar date.
  if (value instanceof Date) return formatLocalDate(value);
  // `medium` is stated rather than inherited: this is the DISPLAY formatter, so
  // it owns a presentation decision and should not pick one up from a default a
  // machine-facing caller also reads (#11426).
  //
  // The clipboard and the client-side export resolve the same value through the
  // same function (`cellFormatting.ts`, also `medium`), so a cell cannot copy
  // out as a different DAY than the one on screen — which is the invariant that
  // matters. Only the presentation is chosen here.
  return formatDateCellScalar(value, "medium", unit) ?? String(value);
};
